import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  Flex,
  Select,
  Spinner,
  useColorModeValue
} from '@chakra-ui/react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';

const EngagementChart = ({ data, campaignId }) => {
  const [chartType, setChartType] = useState('line');
  const [chartData, setChartData] = useState([]);
  
  const gridColor = useColorModeValue('#E2E8F0', '#2D3748');
  const textColor = useColorModeValue('#4A5568', '#CBD5E0');

  useEffect(() => {
    // Format data for the chart
    if (data && data.length > 0) {
      const formattedData = data.map(item => ({
        date: formatChartDate(item.date),
        cliques: item.clicks,
        // Add mock open data (in real scenario, you'd fetch this too)
        aberturas: Math.floor(item.clicks * 2.5 + Math.random() * 10)
      }));
      setChartData(formattedData);
    } else {
      // Generate sample data if none exists
      const sampleData = generateSampleData();
      setChartData(sampleData);
    }
  }, [data]);

  const formatChartDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'short' 
    });
  };

  const generateSampleData = () => {
    const days = 7;
    const data = [];
    const today = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      data.push({
        date: formatChartDate(date.toISOString()),
        aberturas: Math.floor(Math.random() * 100) + 20,
        cliques: Math.floor(Math.random() * 40) + 5
      });
    }
    
    return data;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box bg="white" p={3} borderRadius="md" shadow="lg" border="1px" borderColor="gray.200">
          <Text fontWeight="bold" fontSize="sm">{label}</Text>
          {payload.map((entry, index) => (
            <Text key={index} fontSize="sm" color={entry.color}>
              {entry.name}: {entry.value}
            </Text>
          ))}
        </Box>
      );
    }
    return null;
  };

  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    const commonAxisProps = {
      stroke: textColor,
      style: { fontSize: 12 }
    };

    switch (chartType) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="colorAberturas" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#805AD5" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#805AD5" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorCliques" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#DD6B20" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#DD6B20" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="date" {...commonAxisProps} />
            <YAxis {...commonAxisProps} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Area 
              type="monotone" 
              dataKey="aberturas" 
              stroke="#805AD5" 
              fillOpacity={1} 
              fill="url(#colorAberturas)"
              name="Aberturas"
            />
            <Area 
              type="monotone" 
              dataKey="cliques" 
              stroke="#DD6B20" 
              fillOpacity={1} 
              fill="url(#colorCliques)"
              name="Cliques"
            />
          </AreaChart>
        );
      
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="date" {...commonAxisProps} />
            <YAxis {...commonAxisProps} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="aberturas" fill="#805AD5" name="Aberturas" />
            <Bar dataKey="cliques" fill="#DD6B20" name="Cliques" />
          </BarChart>
        );
      
      default: // line
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="date" {...commonAxisProps} />
            <YAxis {...commonAxisProps} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="aberturas" 
              stroke="#805AD5" 
              strokeWidth={2}
              dot={{ fill: '#805AD5', r: 4 }}
              activeDot={{ r: 6 }}
              name="Aberturas"
            />
            <Line 
              type="monotone" 
              dataKey="cliques" 
              stroke="#DD6B20" 
              strokeWidth={2}
              dot={{ fill: '#DD6B20', r: 4 }}
              activeDot={{ r: 6 }}
              name="Cliques"
            />
          </LineChart>
        );
    }
  };

  if (chartData.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="400px">
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  return (
    <Box bg="white" p={6} borderRadius="lg" shadow="sm">
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="md">Engajamento ao Longo do Tempo</Heading>
        <Select 
          value={chartType} 
          onChange={(e) => setChartType(e.target.value)}
          width="150px"
          size="sm"
        >
          <option value="line">Linha</option>
          <option value="area">Área</option>
          <option value="bar">Barras</option>
        </Select>
      </Flex>

      <ResponsiveContainer width="100%" height={400}>
        {renderChart()}
      </ResponsiveContainer>

      <Box mt={4} p={3} bg="gray.50" borderRadius="md">
        <Text fontSize="sm" color="gray.600">
          <strong>Nota:</strong> Este gráfico mostra o engajamento dos destinatários ao longo do tempo. 
          As aberturas representam quantas vezes o email foi aberto, e os cliques mostram a interação com links no email.
        </Text>
      </Box>
    </Box>
  );
};

export default EngagementChart;